import { JIDRCurrency } from "@components/CurrencyIcon/CurrencyIcon";
import Header from "@components/Header/Header";
import { JagadLogo } from "@components/JagadLogo/JagadLogo";
import PrimaryButton from "@components/PrimaryButton/PrimaryButton";
import TransactionDetailStatusIcon from "@components/TransactionDetailStatusIcon/TransactionDetailStatusIcon";
import { formatDate, toCurrencyFormat } from "@lib/utils";
import React, { useRef, useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import backgroundImage from '@assets/images/HomeBackground.png';
import { hapticFeedback, setHeaderColor, getPlatform } from '@lib/telegram';

import {
    Container,
    HeaderContainer,
    HeaderBackground,
    Body,
    ButtonContainer,
    StatusContainer,
    StatusText,
    CurrencyText,
    TextValue,
    TimestampText,
    RowContainer,
    RowLabel,
    Card,
    CardContent,
    Divider,
    PendingMessage,
    AmountContainer,
    AmountLabel,
    AmountValueContainer,
    StatusIconContainer,
    RefreshIndicatorContainer,
    ButtonRowContainer
} from "./BankTransferPaymentReceipt.styles";
import BigStatusIcon from "@components/BigStatusIcon/BigStatusIcon";
import useActivityDetail from "@hooks/useActivityDetail";
import LoadingModal from "@components/LoadingModal/LoadingModal";
import ActivityIndicator from "@components/ActivityIndicator/ActivityIndicator";
import { usePDFShare } from "@hooks/usePDFShare";
import { PRIMARY, SKY_BLUE } from "@styles/colors";
import ShareIcon from "@components/ShareIcon/ShareIcon";
import SmallButtonWithIcon from "@components/SmallButtonWithIcon/SmallButtonWithIcon";

const BankTransferPaymentReceipt = () => {
    const location = useLocation();
    const { activity_uuid } = location.state || {};

    const { data, isLoading, refetch } = useActivityDetail({
        uuid: activity_uuid,
        code: 301,
        fetchImmediately: true,
    })

    const { t } = useTranslation();
    const navigate = useNavigate();

    // Pull-to-refresh state and refs
    const [touchStartY, setTouchStartY] = useState<number | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isButtonPressedLoading, setIsButtonPressedLoading] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null);
    const lastHapticTimeRef = useRef(0);

    // the order of the data is important
    const receipt = {
        status: data?.status,
        bank: data?.bank,
        account_number: data?.account,
        account_name: data?.account_name,
        timestamp: data?.timestamp,
        transaction_id: data?.uuid,
        amount: data?.ui_amount,
        fee: data?.fee,
        total: data?.total_amount,
        memo: data?.memo,
    }

    // PDF sharing hook
    const { shareOrDownloadPDF, isShareLoading } = usePDFShare(
        data ? {
            transactionId: data.uuid,
            transactionDate: data.timestamp,
            amount: data.ui_amount,
            bank: data.bank,
            accountNumber: data.account,
            accountName: data.account_name,
            status: data.status,
            fees: (Number(data.fee || 0)).toString(),
            totalAmount: (Number(data.ui_amount || 0) + Number(data.fee || 0)).toString()
        } : null,
        isLoading
    )

    const handleShareAndDownload = async () => {
        setIsButtonPressedLoading(true)
        try {
            await shareOrDownloadPDF()
        } finally {
            setIsButtonPressedLoading(false)
        }
    }

    useEffect(() => {
        setHeaderColor(SKY_BLUE)
    }, [])

    // Handle the refresh action
    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await refetch();
        } catch (error) {

        } finally {
            setIsRefreshing(false);
        }
    };

    // Handle touch start event
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        setTouchStartY(e.touches[0].clientY);
    }, []);

    // Handle touch move event
    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (touchStartY === null) return;

        const touchY = e.touches[0].clientY;
        const deltaY = touchStartY - touchY;

        // Check if we're at the top of the container
        const isAtTop = containerRef.current && containerRef.current.scrollTop <= 0;

        // If user is trying to scroll up (dragging finger down)
        if (deltaY < 0 && isAtTop) {
            // If pull distance exceeds threshold, trigger haptic feedback
            if (Math.abs(deltaY) > 300) {
                // Get current time
                const now = Date.now();

                // Only trigger haptic feedback if it's been at least 1 second since the last trigger
                if (now - lastHapticTimeRef.current > 1000) {
                    // Trigger haptic feedback
                    hapticFeedback('heavy');

                    // Update the last haptic time
                    lastHapticTimeRef.current = now;

                    // Refresh data
                    handleRefresh();
                }
            }
        }
    }, [touchStartY, refetch]);

    // Handle touch end event
    const handleTouchEnd = useCallback(() => {
        setTouchStartY(null);
    }, []);

    return (
        <Container
            ref={containerRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <HeaderContainer>
                <Header
                    $transparent
                >
                    <JagadLogo type="logo-color-text-black" size={24} />
                </Header>
            </HeaderContainer>

            <HeaderBackground
                src={backgroundImage}
                alt="Light gradient background"
            />
            {/* Pull-to-refresh indicator */}
            {isRefreshing && (
                <RefreshIndicatorContainer>
                    <ActivityIndicator visible={true} size={24} />
                </RefreshIndicatorContainer>
            )}

            {/* Loading modal for initial load */}
            <LoadingModal visible={(isLoading && !isRefreshing) || isButtonPressedLoading || isShareLoading} />

            <Body>
                <ReceiptCard data={receipt} />
            </Body>

            <ButtonContainer>
                {/* Hide share button on Android - Android WebView doesn't support Web Share API */}
                {receipt.status === 'COMPLETED' && getPlatform() !== 'android' && (
                    <ButtonRowContainer>
                        <SmallButtonWithIcon
                            strokeWidth={1}
                            icon={<ShareIcon type={2} fill={PRIMARY} />}
                            onPress={handleShareAndDownload}
                        />
                    </ButtonRowContainer>
                )}
                <PrimaryButton
                    onClick={() => navigate('/home')}
                    variant="outline"
                >
                    {t('bankTransferPayment.receipt.backToHome')}
                </PrimaryButton>
            </ButtonContainer>

        </Container>
    );
};

export default BankTransferPaymentReceipt;

const ReceiptCardRowValue = ({
    type,
    value,
    boldValue
}: {
    type: 'status' | 'currency' | 'text' | 'timestamp'
    value: string | number
    boldValue?: boolean
}) => {
    const { t } = useTranslation();
    switch (type) {
        case 'status':
            return (
                <StatusContainer>
                    <StatusText $isCompleted={value === 'COMPLETED'}>
                        {value === 'COMPLETED' ? t('bankTransferPayment.receipt.status.completed') : t('bankTransferPayment.receipt.status.pending')}
                    </StatusText>
                    <TransactionDetailStatusIcon status={value === 'COMPLETED' ? 'COMPLETED' : 'PENDING'} />
                </StatusContainer>
            );
        case 'currency':
            return (
                <CurrencyText $boldValue={boldValue}>
                    {!isNaN(Number(value)) ? `Rp${toCurrencyFormat(Number(value))}` : value}
                </CurrencyText>
            );
        case 'timestamp':
            return (
                <TimestampText $boldValue={boldValue}>
                    {formatDate(value as string, true)}
                </TimestampText>
            );
        default:
            return (
                <TextValue $boldValue={boldValue}>
                    {value}
                </TextValue>
            );
    }
};

const ReceiptCardRow = ({
    label,
    value,
    type,
    boldValue
}: {
    label: string;
    value: string | number;
    type: 'status' | 'currency' | 'text' | 'timestamp'
    boldValue?: boolean
}) => {
    return (
        <RowContainer>
            <RowLabel>{label}</RowLabel>
            <ReceiptCardRowValue type={type} value={value} boldValue={boldValue} />
        </RowContainer>
    );
};

type ReceiptDataProps = {
    status: 'COMPLETED' | string
    bank: string
    account_number: string
    account_name: string
    timestamp: string
    transaction_id: string
    amount?: number
    fee?: number
    total?: number
    memo?: string
}

const ReceiptCard = ({
    data
}: {
    data: ReceiptDataProps
}) => {
    const { t } = useTranslation();
    return (
        <Card>
            <StatusIconContainer>
                <BigStatusIcon status={data.status} />
            </StatusIconContainer>
            <ReceiptAmount number={data.total || data.amount || 0} />
            <CardContent>
                {Object.entries(data).map(([key, value]) => {
                    if (!value) return null;
                    let type: 'status' | 'currency' | 'text' | 'timestamp' = 'text';
                    if (key === 'status') type = 'status';
                    if (key === 'timestamp') type = 'timestamp';
                    if (['amount', 'fee', 'total'].includes(key)) type = 'currency';

                    return (
                        <React.Fragment key={key}>
                            {key.toLocaleLowerCase() === 'amount' && <Divider />}
                            <ReceiptCardRow
                                key={key}
                                label={t(`bankTransferPayment.receipt.receiptFields.${key.toLowerCase()}`)}
                                value={value}
                                type={type}
                                boldValue={key === 'total'}
                            />
                            {key.toLocaleLowerCase() === 'fee' && <Divider />}
                        </React.Fragment>
                    )
                })}
            </CardContent>

            {data.status !== 'COMPLETED' &&
                <React.Fragment>
                    <Divider />
                    <PendingMessage>
                        {t('bankTransferPayment.receipt.pendingMessage')}
                    </PendingMessage>
                </React.Fragment>
            }
        </Card>
    )
}

const ReceiptAmount = ({
    number
}: {
    number: number
}) => {
    const { t } = useTranslation();
    return (
        <AmountContainer>
            <AmountLabel>
                {t('bankTransferPayment.receipt.amount')}
            </AmountLabel>
            <AmountValueContainer>
                <JIDRCurrency />
                <span>{toCurrencyFormat(number)}</span>
            </AmountValueContainer>
        </AmountContainer>
    )
}

